import React, { useState, useEffect, useRef, useCallback } from 'react';
import ResearcherCard from '@/features/researcher/components/ResearcherCard';
import { getData } from '@/features/shared/lib/api';
import Paginator from '@/features/shared/ui/Paginator';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export default function SavedResearcher() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ fromPage: 1, totalPage: 1, resultsPerPage: 9, totalResults: 0 });
  const [sort, setSort] = useState({ order_by: 'date', order: 'desc' });
  const listSectionRef = useRef(null);
  // 첫 로딩인지 추적 (첫 API 호출일 때만 로딩 컴포넌트 표시)
  const isFirstLoadRef = useRef(true);
  // 현재 사용자의 scholar_id (마이페이지에서는 rmVars.scholar_id 사용)
  const userId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;

  // fetchList 함수를 외부로 분리하여 재사용 가능하도록
  const fetchList = useCallback(async () => {
    if (!userId) return;

    try {
      // 첫 로딩일 때만 loading 상태를 true로 설정
      if (isFirstLoadRef.current) {
        setLoading(true);
      }
      setErr(null);
      const res = await getData('/rm/v1/scholars', {
        current_page: page,
        record_per_page: 9,
        ks: '',
        sub_ks: '',
        order_by: sort.order_by,
        order: sort.order,
        favor_scholar_id: userId,
      });
      setRows(res?.records || []);
      // pageInfo 업데이트
      if (res?.pageInfo) {
        setPageInfo({
          fromPage: res.pageInfo.current_page ?? res.pageInfo.fromPage ?? page,
          totalPage: res.pageInfo.totalPage ?? res.pageInfo.total_page ?? 1,
          resultsPerPage: res.pageInfo.resultsPerPage ?? res.pageInfo.per_page ?? 9,
          totalResults: res.pageInfo.totalResults ?? res.pageInfo.total_results ?? 0,
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally {
      if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false; // 첫 로딩 완료 후 플래그 해제
      }
    }
  }, [userId, page, sort]);

  useEffect(() => {
    let mounted = true;
    fetchList();
    return () => { mounted = false; };
  }, [fetchList]);

  // sort 옵션 값 계산
  const sortValue = (() => {
    const ob = sort?.order_by;
    const od = sort?.order;
    if (ob === 'name') return `name:${od || 'asc'}`;
    if (ob === 'doc_count') return 'doc_count:desc';
    if (ob === 'h_index') return 'h_index:desc';
    // default
    return 'date:desc';
  })();

  // sort 변경 핸들러
  const handleSortChange = (e) => {
    const val = String(e.target.value);
    let next = { order_by: 'date', order: 'desc' };
    if (val === 'date:desc') next = { order_by: 'date', order: 'desc' };
    else if (val === 'name:asc') next = { order_by: 'name', order: 'asc' };
    else if (val === 'name:desc') next = { order_by: 'name', order: 'desc' };
    else if (val === 'doc_count:desc') next = { order_by: 'doc_count', order: 'desc' };
    else if (val === 'h_index:desc') next = { order_by: 'h_index', order: 'desc' };
    setSort(next);
    setPage(1); // 정렬 변경 시 첫 페이지로
  };

  return (
    <div>
      <div className="flex justify-between items-center w-full">
        <h3 className="mypage-subpage-title text-[26px] lg:text-[24px] font-black lg:font-bold leading-[130%] lg:leading-[100%]">Saved Researcher</h3>
      </div>

      <div className="listUtil mt-10 lg:flex items-center justify-between">
        <div className="mb-3 text-[14px] text-[#6C7687] lg:mb-0">
          Found <strong>{(pageInfo.totalResults && Number(pageInfo.totalResults).toLocaleString()) || 0}</strong> results
        </div>

        {/*<div className="sort lg:flex items-center gap-3 text-[13px] text-[#6C7687] font-medium">*/}
        {/*  <p className="hidden lg:flex items-center gap-1">*/}
        {/*    <img src={rmVars.themeUrl + 'images/filter_list_ico.png'} className="w-4 h-4" alt="sort icon" />*/}
        {/*    Sort by*/}
        {/*  </p>*/}

        {/*  <select*/}
        {/*    value={sortValue}*/}
        {/*    onChange={handleSortChange}*/}
        {/*    className="w-full py-2 px-3 bg-white border border-[#E4E8F1] rounded-lg appearance-none text-[13px] text-[#6C7687] outline-none lg:w-[184px]"*/}
        {/*    style={{*/}
        {/*      backgroundImage: `url(${rmVars.themeUrl}images/expand_more_8D9AAF.png)`,*/}
        {/*      backgroundRepeat: 'no-repeat',*/}
        {/*      backgroundSize: '18px',*/}
        {/*      backgroundPosition: '98%',*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    <option className="bg-white" value="date:desc">Recently Published</option>*/}
        {/*    <option className="bg-white" value="name:asc">Name : A to Z</option>*/}
        {/*    <option className="bg-white" value="name:desc">Name : Z to A</option>*/}
        {/*    <option className="bg-white" value="doc_count:desc">Doc Count : highest first</option>*/}
        {/*    <option className="bg-white" value="h_index:desc">H-index : highest first</option>*/}
        {/*  </select>*/}
        {/*</div>*/}
      </div>

      {err && <div className="mb-4 text-red-600 text-sm">에러: {String(err)}</div>}

      <div className="mypage-card mt-8 lg:mt-6 lg:p-[60px] bg-white rounded-[12px]" ref={listSectionRef}>
        {/* <div className="year-filter flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] leading-[100%] text-[#6C7687] rounded-full">
          <button className="h-[27px] px-3 rounded-full cursor-pointer active bg-[#E4E8F1]">Professor</button>
          <button className="h-[27px] px-3 rounded-full cursor-pointer bg-[#E4E8F1]">Graduate Student</button>
        </div> */}

        <div className="relative mt-4 lg:mt-3">
          {/* 로딩 오버레이 - 첫 로딩일 때만 표시 */}
          {isFirstLoadRef.current && loading && <ChartSkeletonLoader show={loading} />}

          {/* 데이터 목록 - 로딩 중에도 기존 데이터 표시 */}
          {rows.length > 0 && (
            <ul className="w-full flex flex-col gap-3 min-[800px]:flex-row min-[800px]:flex-wrap min-[800px]:gap-[28px]">
              {rows.map((item) => (
                <ResearcherCard
                  key={item.scholar_id || item.id}
                  item={item}
                  yearDisplay="text"
                  yearRange={{ start: '2020', end: '2024' }}
                  liClassName="relative p-8 pb-[14px] bg-white border border-[#E4E8F1] rounded-xl min-[800px]:w-[calc((100%-28px)/2)] xl:w-[calc((100%-56px)/3)] xl:px-[30px] xl:pt-[26px] xl:pb-4 2xl:w-[calc((100%-84px)/4)] hover:shadow-[0px_4px_20px_rgba(0,0,0,0.05)] tansition duration-300 cursor-pointer"
                  onFavoriteChange={({ type }) => {
                    // 즐겨찾기 삭제 시 데이터 다시 불러오기
                    if (type === 'delete') {
                      fetchList();
                    }
                  }}
                />
              ))}
            </ul>
          )}

          {/* 데이터 없음 */}
          {rows.length === 0 && !loading && (
            <div className="text-center py-8 text-[#8D9AAF]">
              데이터가 없습니다.
            </div>
          )}

          {/* 페이징 */}
          {pageInfo.totalPage > 1 && (
            <div className="mt-[50px] lg:mt-[40px]">
              <Paginator
                pageInfo={{ ...pageInfo, fromPage: page }}
                onPage={(p) => {
                  setPage(p);
                  // "Saved Researcher" 섹션으로 스크롤
                  if (listSectionRef.current) {
                    setTimeout(() => {
                      try {
                        const element = listSectionRef.current;
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - 100;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      } catch (e) {
                        // no-op
                      }
                    }, 0);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
